import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { FiShield, FiArrowLeft } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";

const AccessDeniedContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  padding: 2rem;
`;

const AccessDeniedCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  text-align: center;
  max-width: 500px;
  width: 100%;

  .icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #dc3545, #c82333);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 2rem;
    color: white;
    font-size: 2rem;
  }

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #2d5016;
    margin-bottom: 1rem;
  }

  p {
    color: #666;
    font-size: 1.125rem;
    line-height: 1.6;
    margin-bottom: 2rem;
  }

  .back-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: #2d5016;
    color: white;
    text-decoration: none;
    border-radius: 8px;
    font-weight: 500;
    transition: background 0.3s ease;

    &:hover {
      background: #1a3a0e;
    }
  }
`;

const AdminProtectedRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <AccessDeniedContainer>
        <AccessDeniedCard>
          <div className="icon">
            <FiShield />
          </div>
          <h1>Loading...</h1>
          <p>Checking your access permissions...</p>
        </AccessDeniedCard>
      </AccessDeniedContainer>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user is admin
  if (user?.role !== "admin") {
    return (
      <AccessDeniedContainer>
        <AccessDeniedCard>
          <div className="icon">
            <FiShield />
          </div>
          <h1>Access Denied</h1>
          <p>
            You don't have permission to access the admin dashboard. This area
            is restricted to administrators only.
          </p>
          <a href="/" className="back-button">
            <FiArrowLeft />
            Back to Home
          </a>
        </AccessDeniedCard>
      </AccessDeniedContainer>
    );
  }

  // User is authenticated and is admin, render the children
  return children;
};

export default AdminProtectedRoute;
